import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Check, Crown, Building2, Users as UsersIcon, Sparkles } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import axios from 'axios';

export default function MembershipPlans() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('institutional'); // individual or institutional

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/membership/plans`);
      setPlans(data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const individualPlans = plans.filter(p => p.plan_type === 'individual');
  const institutionalPlans = plans.filter(p => p.plan_type === 'institutional');
  const displayPlans = selectedType === 'individual' ? individualPlans : institutionalPlans;

  const getPlanIcon = (planName) => {
    if (planName.includes('Professional')) return <UsersIcon className="h-8 w-8" />;
    if (planName.includes('Basic')) return <Building2 className="h-8 w-8" />;
    if (planName.includes('Advanced')) return <Sparkles className="h-8 w-8" />;
    if (planName.includes('Strategic')) return <Crown className="h-8 w-8" />;
    return <Building2 className="h-8 w-8" />;
  };

  const getPlanColor = (planName) => {
    if (planName.includes('Strategic')) return 'border-yellow-500 bg-gradient-to-br from-yellow-50 to-orange-50';
    if (planName.includes('Advanced')) return 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50';
    return 'border-gray-200 bg-white';
  };

  const getPlanAudience = (planName) => {
    if (planName.includes('Strategic')) return 'Meant for Public Ltd. companies, Financial Institutions, CSR divisions of the corporate, Funding organisations, Economical-Social-Environmental development organisations operating at the grass-root level with presence in multiple locations.';
    if (planName.includes('Advanced')) return 'Meant for Pvt. Ltd. companies, service providers to Economical-Social-Environmental development organisations operating at the grass-root level.';
    if (planName.includes('Basic')) return 'Meant for Proprietorship firms, Partnership firms, Economical-Social-Environmental development organisations operating at the grass-root level.';
    return null;
  };

  const handleSelectPlan = (plan) => {
    // Navigate to registration with selected plan
    navigate('/membership/application', { state: { plan: plan } });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E87722] mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading membership plans...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5F8D] text-white py-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">
              Join the CSED Network
            </h1>
            <p className="text-lg sm:text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Become part of India's premier network for socio-economic development. 
              Access exclusive research, government schemes, and connect with changemakers.
            </p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12">
          {/* Membership Type Toggle */}
          <div className="flex justify-center mb-12">
            <div className="bg-white rounded-lg p-2 shadow-md inline-flex">
              <button
                onClick={() => setSelectedType('individual')}
                className={`px-8 py-3 rounded-md font-medium transition-all ${
                  selectedType === 'individual'
                    ? 'bg-[#E87722] text-white'
                    : 'text-gray-600 hover:text-[#E87722]'
                }`}
              >
                <UsersIcon className="h-5 w-5 inline mr-2" />
                Individual
              </button>
              <button
                onClick={() => setSelectedType('institutional')}
                className={`px-8 py-3 rounded-md font-medium transition-all ${
                  selectedType === 'institutional'
                    ? 'bg-[#E87722] text-white'
                    : 'text-gray-600 hover:text-[#E87722]'
                }`}
              >
                <Building2 className="h-5 w-5 inline mr-2" />
                Institutional
              </button>
            </div>
          </div>

          {/* Plans Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {displayPlans.map((plan) => (
              <Card
                key={plan.id}
                className={`relative p-8 hover:shadow-2xl transition-all duration-300 border-2 ${getPlanColor(plan.plan_name)}`}
              >
                {/* Popular Badge for Strategic */}
                {plan.plan_name.includes('Strategic') && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-[#E87722] text-white px-4 py-1 text-sm">
                      Most Popular
                    </Badge>
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className="flex justify-center mb-4 text-[#E87722]">
                    {getPlanIcon(plan.plan_name)}
                  </div>
                  <h3 className="text-2xl font-bold text-[#1E3A5F] mb-2">
                    {plan.plan_name}
                  </h3>
                  {plan.tier_level !== null && plan.tier_level > 0 && (
                    <p className="text-sm text-gray-600">Tier {plan.tier_level}</p>
                  )}
                </div>

                {/* Pricing */}
                <div className="text-center mb-8">
                  {plan.registration_fee ? (
                    <div>
                      <div className="mb-3">
                        <p className="text-sm font-semibold text-gray-600 mb-1">One-time Registration</p>
                        <p className="text-3xl font-bold text-[#1E3A5F]">
                          ₹{plan.registration_fee.toLocaleString('en-IN')}
                        </p>
                      </div>
                      <div className="border-t pt-3">
                        <p className="text-sm font-semibold text-gray-600 mb-1">Annual Fee</p>
                        <p className="text-3xl font-bold text-[#E87722]">
                          ₹{plan.price.toLocaleString('en-IN')}
                        </p>
                        <p className="text-xs text-gray-500">per year</p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-5xl font-bold text-[#1E3A5F] mb-2">
                        ₹{plan.price.toLocaleString('en-IN')}
                      </p>
                      <p className="text-sm text-gray-600">per year</p>
                    </div>
                  )}
                </div>

                {/* Target Audience */}
                {plan.plan_type === 'institutional' && getPlanAudience(plan.plan_name) && (
                  <div className="mb-6 bg-[#1E3A5F]/5 border border-[#1E3A5F]/10 rounded-lg p-4" data-testid={`plan-audience-${plan.id}`}>
                    <p className="text-sm text-gray-700 italic leading-relaxed">
                      {getPlanAudience(plan.plan_name)}
                    </p>
                  </div>
                )}

                {/* Features */}
                <div className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <Button
                  onClick={() => handleSelectPlan(plan)}
                  className={`w-full ${
                    plan.plan_name.includes('Strategic')
                      ? 'bg-[#E87722] hover:bg-[#d66a1a]'
                      : 'bg-[#1E3A5F] hover:bg-[#2d5a8f]'
                  }`}
                >
                  Get Started
                </Button>
              </Card>
            ))}
          </div>

          {/* Features Comparison */}
          <div className="mt-16 max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-[#1E3A5F] text-center mb-8">
              What's Included
            </h2>
            
            <Card className="p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-bold text-lg text-[#1E3A5F] mb-4">All Members Get:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700">Full access to Government Schemes Platform</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700">Save & bookmark schemes and research</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700">Personalized recommendations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700">Member communications & updates</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-bold text-lg text-[#1E3A5F] mb-4">Premium Features:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-[#E87722] flex-shrink-0" />
                      <span className="text-gray-700">Unlimited research paper access</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-[#E87722] flex-shrink-0" />
                      <span className="text-gray-700">Publish research & insights</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-[#E87722] flex-shrink-0" />
                      <span className="text-gray-700">Priority M&E consultation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-[#E87722] flex-shrink-0" />
                      <span className="text-gray-700">Policy dialogue participation</span>
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>

          {/* Institutional Plan Comparison Table */}
          {institutionalPlans.length > 0 && (
            <div className="mt-16 max-w-6xl mx-auto" data-testid="plan-comparison-table">
              <h2 className="text-3xl font-bold text-[#1E3A5F] text-center mb-8">
                Compare Institutional Plans
              </h2>
              <Card className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#1E3A5F] text-white">
                      <th className="text-left p-4 font-semibold w-1/3">Feature</th>
                      {institutionalPlans.map((plan) => (
                        <th key={plan.id} className="p-4 text-center font-semibold">
                          {plan.plan_name.replace('Institutional ', '')}
                          {plan.plan_name.includes('Strategic') && (
                            <Badge className="bg-[#E87722] text-white ml-2 text-[10px]">Popular</Badge>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b bg-gray-50">
                      <td className="p-4 font-medium text-gray-700">One-time Registration</td>
                      {institutionalPlans.map((plan) => (
                        <td key={plan.id} className="p-4 text-center font-bold text-[#1E3A5F]">
                          ₹{(plan.registration_fee || 0).toLocaleString('en-IN')}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="p-4 font-medium text-gray-700">Annual Fee</td>
                      {institutionalPlans.map((plan) => (
                        <td key={plan.id} className="p-4 text-center font-bold text-[#E87722]">
                          ₹{plan.price.toLocaleString('en-IN')}<span className="text-xs text-gray-500 font-normal">/yr</span>
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b bg-gray-50">
                      <td className="p-4 font-medium text-gray-700">Meant For</td>
                      {institutionalPlans.map((plan) => (
                        <td key={plan.id} className="p-4 text-center text-xs text-gray-600 align-top">
                          {getPlanAudience(plan.plan_name)?.replace('Meant for ', '')}
                        </td>
                      ))}
                    </tr>
                    {[...new Set(institutionalPlans.flatMap(p => p.features))].map((feature, idx) => (
                      <tr key={idx} className={`border-b ${idx % 2 === 1 ? 'bg-gray-50' : ''}`}>
                        <td className="p-4 text-gray-700">{feature}</td>
                        {institutionalPlans.map((plan) => (
                          <td key={plan.id} className="p-4 text-center">
                            {plan.features.includes(feature) ? (
                              <Check className="h-5 w-5 text-green-600 mx-auto" />
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr>
                      <td className="p-4"></td>
                      {institutionalPlans.map((plan) => (
                        <td key={plan.id} className="p-4 text-center">
                          <Button
                            size="sm"
                            onClick={() => handleSelectPlan(plan)}
                            className={plan.plan_name.includes('Strategic') ? 'bg-[#E87722] hover:bg-[#d66a1a]' : 'bg-[#1E3A5F] hover:bg-[#2d5a8f]'}
                            data-testid={`comparison-select-${plan.id}`}
                          >
                            Get Started
                          </Button>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </Card>
            </div>
          )}

          {/* CTA Section */}
          <div className="mt-16 text-center">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">
              Questions about membership?
            </h2>
            <p className="text-gray-600 mb-6">
              Our team is here to help you choose the right plan for your needs.
            </p>
            <Button variant="outline" onClick={() => navigate('/engage')}>
              Contact Us
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
